import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BadgeCheck, Search, Save, AlertTriangle, ChevronLeft, ChevronRight, Play } from 'lucide-react';
import {
  getContractLicensePlan,
  saveContractLicensePlan,
  applyContractLicensePlan,
  getLicensePlanApplyStatus,
  getPublicContractLicensePlan,
  savePublicContractLicensePlan
} from '../firebase/api';

const PAGE_SIZE = 20;

/** Starter / Standard / Plus / Enterprise Plus — nunca recortar Enterprise a "Plus". */
function shortSkuLabel(name, kind) {
  const raw = String(name || '');
  if (kind === 'addon' || /gemini|ai expanded|ai ultra|^ge\b/i.test(raw)) {
    if (/ai expanded/i.test(raw)) return 'AI Expanded';
    if (/ai ultra/i.test(raw)) return 'Gemini Ultra';
    if (/gemini for workspace|gemini in workspace/i.test(raw)) return 'Gemini';
    return raw
      .replace(/gemini enterprise/ig, 'GE')
      .replace(/edici[oó]n/ig, '')
      .replace(/\s+/g, ' ')
      .trim() || 'GE';
  }
  return raw
    .replace(/^Google Workspace\s+/i, '')
    .replace(/^Business\s+/i, '')
    .trim() || name;
}

function assignmentFingerprint(list) {
  return (list || []).map((u) => (
    `${u.email}:${u.intendedSkuId || ''}:${(u.addonSkuIds || []).slice().sort().join(',')}`
  )).join('|');
}

/** El plan ya vive en el contrato. Pintarlo evita esperar la hoja de Google. */
function viewFromSavedPlan(plan) {
  if (!plan || !Array.isArray(plan.users) || plan.users.length === 0) return null;
  const skus = plan.skus || [];
  return {
    mixed: Boolean(plan.mixed),
    skuContext: {
      mixed: Boolean(plan.mixed),
      bootstrapSku: plan.bootstrapSkuId
        ? {
          gws_id: plan.bootstrapSkuId,
          friendly_name: skus.find((s) => s.gws_id === plan.bootstrapSkuId)?.friendly_name || ''
        }
        : null,
      skus,
      addons: plan.addons || []
    },
    users: plan.users,
    plan,
    applyReport: null
  };
}

/**
 * Paso 4 de Implementación (staff) o carga de usuarios (cliente).
 * Refero: Raycast/Fernand fila compacta; Linear toolbar.
 * mode=client: mismo mapa, sin Aplicar en Google.
 */
export default function LicensePlanPanel({
  contract,
  showToast,
  onPatched,
  mode = 'staff',
  onClientDone
}) {
  const isClient = mode === 'client';
  const contractId = contract?.id;
  const seeded = viewFromSavedPlan(contract?.licensePlan);
  const [loading, setLoading] = useState(!seeded);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(seeded);
  const [users, setUsers] = useState(seeded?.users || []);
  const [query, setQuery] = useState('');
  const [skuFilter, setSkuFilter] = useState(null);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(() => new Set());
  const [savedFp, setSavedFp] = useState(() => assignmentFingerprint(seeded?.users || []));
  const [error, setError] = useState(null);
  const [applying, setApplying] = useState(false);
  const [progress, setProgress] = useState(null);
  const [report, setReport] = useState(contract?.licensePlanApplyReport || null);
  const applyPollRef = useRef(null);
  const paintedRef = useRef(Boolean(seeded));

  const stopApplyPoll = () => {
    if (applyPollRef.current) {
      clearInterval(applyPollRef.current);
      applyPollRef.current = null;
    }
  };

  const load = async () => {
    if (!contractId) return;
    if (!paintedRef.current) {
      setLoading(true);
      setError(null);
    }
    try {
      const res = isClient
        ? await getPublicContractLicensePlan(contractId)
        : await getContractLicensePlan(contractId);
      paintedRef.current = true;
      setData(res);
      const nextUsers = res.users || res.plan?.users || [];
      setUsers(nextUsers);
      setSavedFp(assignmentFingerprint(nextUsers));
      setSelected(new Set());
      setPage(1);
      setError(null);
      if (!isClient) {
        setReport(res.applyReport || contract?.licensePlanApplyReport || null);
      }
    } catch (e) {
      if (!paintedRef.current) setError(e.message || 'No se pudo cargar el plan.');
    } finally {
      setLoading(false);
    }
  };

  const seenContractRef = useRef(contractId);

  useEffect(() => {
    if (seenContractRef.current !== contractId) {
      seenContractRef.current = contractId;
      const next = viewFromSavedPlan(contract?.licensePlan);
      paintedRef.current = Boolean(next);
      setSelected(new Set());
      setPage(1);
      setQuery('');
      setSkuFilter(null);
      if (next) {
        setData(next);
        setUsers(next.users);
        setSavedFp(assignmentFingerprint(next.users));
        setLoading(false);
        setError(null);
      } else {
        setData(null);
        setUsers([]);
        setSavedFp('');
        setLoading(true);
        setError(null);
      }
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractId, contract?.lista_usuarios_url, contract?.userListSheetUrl]);

  useEffect(() => {
    if (paintedRef.current) return;
    const next = viewFromSavedPlan(contract?.licensePlan);
    if (!next) return;
    paintedRef.current = true;
    setData(next);
    setUsers(next.users);
    setSavedFp(assignmentFingerprint(next.users));
    setLoading(false);
  }, [contract?.licensePlan, contractId]);

  useEffect(() => () => stopApplyPoll(), []);

  const skuContext = data?.skuContext;
  const bootstrapId = skuContext?.bootstrapSku?.gws_id;
  const skus = skuContext?.skus || [];
  const addons = skuContext?.addons || [];

  const quotas = useMemo(() => {
    const counts = {};
    const addonCounts = {};
    for (const u of users) {
      const id = u.intendedSkuId || bootstrapId;
      if (id) counts[id] = (counts[id] || 0) + 1;
      for (const addonId of u.addonSkuIds || []) {
        addonCounts[addonId] = (addonCounts[addonId] || 0) + 1;
      }
    }
    const workspace = skus.map((s) => ({
      ...s,
      kind: 'workspace',
      assigned: counts[s.gws_id] || 0,
      over: s.gws_id !== bootstrapId && (counts[s.gws_id] || 0) > (Number(s.qty) || 0)
    }));
    const extra = addons.map((s) => ({
      ...s,
      kind: 'addon',
      assigned: addonCounts[s.gws_id] || 0,
      over: (addonCounts[s.gws_id] || 0) > (Number(s.qty) || 0)
    }));
    return [...workspace, ...extra];
  }, [users, skus, addons, bootstrapId]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return users.filter((u) => {
      if (skuFilter) {
        const addonHit = (u.addonSkuIds || []).includes(skuFilter);
        const id = u.intendedSkuId || bootstrapId;
        if (id !== skuFilter && !addonHit) return false;
      }
      if (!q) return true;
      return (u.email || '').toLowerCase().includes(q)
        || (u.name || '').toLowerCase().includes(q);
    });
  }, [users, query, skuFilter, bootstrapId]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const pageUsers = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const rangeStart = filtered.length === 0 ? 0 : ((safePage - 1) * PAGE_SIZE) + 1;
  const rangeEnd = Math.min(safePage * PAGE_SIZE, filtered.length);

  useEffect(() => {
    setPage(1);
  }, [query, skuFilter]);

  const allPageSelected = pageUsers.length > 0 && pageUsers.every((u) => selected.has(u.email));
  const overQuota = quotas.some((q) => q.over);
  const persisted = Boolean(contract?.licensePlan?.updatedAt || data?.plan?.updatedBy);
  const dirty = !persisted || assignmentFingerprint(users) !== savedFp;
  const canApply = persisted && !dirty && !overQuota && !applying && users.length > 0;

  if (!loading && data && data.mixed === false) return null;

  const setSkuForEmails = (emails, skuId) => {
    const set = new Set(emails);
    setUsers((prev) => prev.map((u) => (
      set.has(u.email) ? { ...u, intendedSkuId: skuId, source: 'ui' } : u
    )));
  };

  const toggleAddonForEmails = (emails, addonId) => {
    const set = new Set(emails);
    setUsers((prev) => prev.map((u) => {
      if (!set.has(u.email)) return u;
      const current = new Set(u.addonSkuIds || []);
      if (current.has(addonId)) current.delete(addonId);
      else current.add(addonId);
      return { ...u, addonSkuIds: Array.from(current), source: 'ui' };
    }));
  };

  const toggleSkuFilter = (id) => {
    setSkuFilter((prev) => (prev === id ? null : id));
  };

  const handleSave = async () => {
    if (!contractId || saving || overQuota || !users.length) return false;
    try {
      setSaving(true);
      const res = isClient
        ? await savePublicContractLicensePlan(contractId, users)
        : await saveContractLicensePlan(contractId, users);
      setData(res);
      const nextUsers = res.plan?.users || users;
      setUsers(nextUsers);
      setSavedFp(assignmentFingerprint(nextUsers));
      onPatched?.({ licensePlan: res.plan });
      showToast?.(
        isClient
          ? 'Listo. Plexarian usará este mapa al aplicar las licencias.'
          : 'Mapa guardado en Nexus. Google no cambia hasta que lo apliques.',
        'success'
      );
      return true;
    } catch (e) {
      showToast?.(e.message || 'No se pudo guardar el plan.', 'error');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleClientContinue = async () => {
    if (overQuota || !users.length || saving) return;
    if (dirty) {
      const ok = await handleSave();
      if (!ok) return;
    }
    onClientDone?.();
  };

  const handleClientSkip = () => {
    if (saving) return;
    onClientDone?.();
  };

  const recoverApplyAfterTimeout = async () => {
    for (let i = 0; i < 10; i += 1) {
      await new Promise((r) => setTimeout(r, 1200));
      const s = await getLicensePlanApplyStatus(contractId);
      if (s?.job?.status === 'completed' && s.report) return s;
      if (s?.job?.status === 'failed') return s;
      if (s?.job) setProgress(s.job);
    }
    return getLicensePlanApplyStatus(contractId);
  };

  const handleApply = async () => {
    if (!contractId || !canApply) return;
    const ok = window.nexusConfirm
      ? await window.nexusConfirm(
        '¿Aplicar el mapa en Google Workspace?\n\nNexus consulta Reseller. Mueve Workspace y asigna Gemini Enterprise Business a quienes el mapa marque. GE Standard y Plus no se asignan aquí: van por otro proceso.'
      )
      : window.confirm('¿Aplicar el mapa en Google Workspace?');
    if (!ok) return;

    setApplying(true);
    setProgress({
      phase: 'reseller',
      label: 'Consultando Reseller…',
      done: 0,
      total: users.length
    });
    stopApplyPoll();
    applyPollRef.current = setInterval(async () => {
      try {
        const s = await getLicensePlanApplyStatus(contractId);
        if (s?.job) setProgress(s.job);
        if (s?.report && s.job?.status === 'completed') setReport(s.report);
      } catch (_) { /* el POST sigue vivo */ }
    }, 800);

    try {
      const res = await applyContractLicensePlan(contractId);
      stopApplyPoll();
      if (res?.report) setReport(res.report);
      if (res?.job) setProgress(res.job);
      onPatched?.({
        licensePlanApplyReport: res.report,
        licensePlanAppliedAt: res.report?.at,
        licensePlanApplyJob: res.job
      });
      const r = res.report || {};
      showToast?.(
        `Google: ${r.moved || 0} movidos, ${r.skipped || 0} ignorados, ${r.failed || 0} con error.`,
        r.failed ? 'error' : 'success'
      );
    } catch (e) {
      stopApplyPoll();
      const timedOut = e.status === 502 || e.status === 504
        || /502|504|Failed to fetch|NetworkError/i.test(e.message || '');
      if (timedOut) {
        const recovered = await recoverApplyAfterTimeout();
        if (recovered?.report) {
          setReport(recovered.report);
          setProgress(recovered.job || { phase: 'done', label: 'Listo' });
          onPatched?.({
            licensePlanApplyReport: recovered.report,
            licensePlanAppliedAt: recovered.report.at
          });
          return;
        }
      }
      const msg = [e.message, e.hint].filter(Boolean).join('\n\n') || 'No se pudo aplicar en Google.';
      if (window.nexusAlert) await window.nexusAlert(msg, 'error');
      else showToast?.(msg, 'error');
    } finally {
      stopApplyPoll();
      setApplying(false);
    }
  };

  return (
    <div className="lic-plan">
      <style>{LIC_PLAN_CSS}</style>
      {loading && <p className="impl-step__empty">Cargando usuarios…</p>}
      {error && <p className="lic-plan__error">{error}</p>}
      {!loading && !error && !users.length && (
        <p className="impl-step__empty">Sube la lista de usuarios primero. Luego puedes marcar quién lleva cada tipo de licencia.</p>
      )}
      {!loading && !error && users.length > 0 && (
        <>
          <div className="lic-plan__toolbar">
            <div className="lic-plan__quotas" role="tablist" aria-label="Cupo por SKU">
              {quotas.map((q) => (
                <button
                  key={q.gws_id}
                  type="button"
                  role="tab"
                  aria-selected={skuFilter === q.gws_id}
                  className={`lic-plan__chip ${q.over ? 'is-over' : ''} ${q.gws_id === bootstrapId && q.kind !== 'addon' ? 'is-base' : ''} ${q.kind === 'addon' ? 'is-addon' : ''} ${skuFilter === q.gws_id ? 'is-on' : ''}`}
                  onClick={() => toggleSkuFilter(q.gws_id)}
                  title={skuFilter === q.gws_id ? 'Ver todos' : `Ver solo ${shortSkuLabel(q.friendly_name, q.kind)}`}
                >
                  <strong>{shortSkuLabel(q.friendly_name, q.kind)}</strong>
                  <span>
                    {q.kind === 'addon' || q.gws_id !== bootstrapId
                      ? `${q.assigned}/${q.qty}`
                      : `${q.assigned} resto`}
                  </span>
                </button>
              ))}
            </div>
            <label className="lic-plan__search">
              <Search size={14} />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar nombre o correo"
              />
            </label>
            {selected.size > 0 && (
              <div className="lic-plan__bulk">
                <span>{selected.size} sel.</span>
                {skus.map((s) => (
                  <button
                    key={s.gws_id}
                    type="button"
                    className="impl-btn impl-btn--ghost"
                    title={`Asignar ${s.friendly_name} a los seleccionados`}
                    onClick={() => {
                      setSkuForEmails(Array.from(selected), s.gws_id);
                      setSelected(new Set());
                    }}
                  >
                    → {shortSkuLabel(s.friendly_name)}
                  </button>
                ))}
                {addons.map((s) => (
                  <button
                    key={s.gws_id}
                    type="button"
                    className="impl-btn impl-btn--ghost"
                    title={`Activar o quitar ${s.friendly_name} en los seleccionados`}
                    onClick={() => {
                      toggleAddonForEmails(Array.from(selected), s.gws_id);
                      setSelected(new Set());
                    }}
                  >
                    ± {shortSkuLabel(s.friendly_name, 'addon')}
                  </button>
                ))}
              </div>
            )}
          </div>
          {isClient && users.length > 0 && (
            <p className="lic-plan__hint">
              {addons.length
                ? 'El plan y Gemini son dos botones. Se pueden dejar los dos encendidos en la misma persona.'
                : 'Quien no cambies se queda en el plan más bajo.'}
            </p>
          )}
          {overQuota && (
            <p className="lic-plan__error">
              <AlertTriangle size={14} /> Hay un SKU por encima del cupo contratado.
            </p>
          )}
          {!isClient && addons.some((a) => a.applyMode === 'external' || a.edition === 'standard' || a.edition === 'plus') && (
            <p className="lic-plan__hint">
              GE Standard y Plus no se asignan desde Implementación: van por otro proceso. GE Business sí.
            </p>
          )}

          <div className="lic-plan__list">
            <div className="lic-plan__list-head">
              <label className="lic-plan__check">
                <input
                  type="checkbox"
                  checked={allPageSelected}
                  onChange={(e) => {
                    setSelected((prev) => {
                      const next = new Set(prev);
                      if (e.target.checked) pageUsers.forEach((u) => next.add(u.email));
                      else pageUsers.forEach((u) => next.delete(u.email));
                      return next;
                    });
                  }}
                  aria-label="Seleccionar esta página"
                />
              </label>
              <span>
                {filtered.length === users.length
                  ? `${users.length} usuario${users.length === 1 ? '' : 's'}`
                  : `${filtered.length} de ${users.length}`}
              </span>
              <span className="lic-plan__col-sku">Licencia</span>
            </div>

            {pageUsers.length === 0 ? (
              <p className="lic-plan__empty">
                {skuFilter
                  ? 'Nadie en este SKU todavía. Quita el filtro y marca a los que suben.'
                  : 'Nadie coincide con la búsqueda.'}
              </p>
            ) : pageUsers.map((u) => {
              const currentId = u.intendedSkuId || bootstrapId || '';
              const addonSet = new Set(u.addonSkuIds || []);
              return (
                <div className="lic-plan__row" key={u.email}>
                  <label className="lic-plan__check">
                    <input
                      type="checkbox"
                      checked={selected.has(u.email)}
                      onChange={(e) => {
                        setSelected((prev) => {
                          const next = new Set(prev);
                          if (e.target.checked) next.add(u.email);
                          else next.delete(u.email);
                          return next;
                        });
                      }}
                      aria-label={`Seleccionar ${u.email}`}
                    />
                  </label>
                  <div className="lic-plan__who">
                    <strong title={u.name || u.email}>{u.name || u.email.split('@')[0]}</strong>
                    <span title={u.email}>{u.email}</span>
                  </div>
                  <div className="lic-plan__skus">
                    <div role="radiogroup" aria-label={`SKU de ${u.email}`} className="lic-plan__skus-group">
                      {skus.map((s) => {
                        const on = currentId === s.gws_id;
                        const upgrade = s.gws_id !== bootstrapId;
                        return (
                          <button
                            key={s.gws_id}
                            type="button"
                            role="radio"
                            aria-checked={on}
                            className={`lic-plan__pill ${on ? 'is-on' : ''} ${upgrade ? 'is-up' : ''}`}
                            onClick={() => setSkuForEmails([u.email], s.gws_id)}
                          >
                            {shortSkuLabel(s.friendly_name)}
                          </button>
                        );
                      })}
                    </div>
                    {addons.length > 0 && (
                      <div className="lic-plan__skus-group" aria-label={`Gemini de ${u.email}`}>
                        {addons.map((s) => {
                          const on = addonSet.has(s.gws_id);
                          return (
                            <button
                              key={s.gws_id}
                              type="button"
                              aria-pressed={on}
                              className={`lic-plan__pill is-addon ${on ? 'is-on' : ''}`}
                              onClick={() => toggleAddonForEmails([u.email], s.gws_id)}
                            >
                              {shortSkuLabel(s.friendly_name, 'addon')}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {pageCount > 1 && (
            <div className="lic-plan__pager">
              <span>{rangeStart}–{rangeEnd} de {filtered.length}</span>
              <div className="lic-plan__pager-btns">
                <button
                  type="button"
                  className="impl-btn impl-btn--ghost impl-btn--icon"
                  disabled={safePage <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  aria-label="Página anterior"
                >
                  <ChevronLeft size={16} />
                </button>
                <button
                  type="button"
                  className="impl-btn impl-btn--ghost impl-btn--icon"
                  disabled={safePage >= pageCount}
                  onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                  aria-label="Página siguiente"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}

          {applying && progress && !isClient && (
            <div className="lic-plan__run" aria-live="polite">
              <div className="lic-plan__run-top">
                <strong>Aplicando en Google</strong>
                <span>
                  {Number(progress.done) || 0}/{Number(progress.total) || users.length || 0}
                </span>
              </div>
              <div className="lic-plan__bar" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.min(100, Math.round(((Number(progress.done) || 0) / Math.max(1, Number(progress.total) || 1)) * 100))}>
                <i style={{ width: `${Math.min(100, Math.round(((Number(progress.done) || 0) / Math.max(1, Number(progress.total) || 1)) * 100))}%` }} />
              </div>
              <p>{progress.label || 'Trabajando…'}</p>
            </div>
          )}

          {!applying && report && !isClient && (
            <div className="lic-plan__report">
              <strong>
                {report.moved || 0} movidos · {report.skipped || 0} ignorados · {report.failed || 0} con error
              </strong>
              <ul>
                {(report.items || [])
                  .filter((item) => item.reason !== 'bootstrap_no_change')
                  .map((item) => (
                    <li key={`${item.email}-${item.type}-${item.reason}`} className={`is-${item.type}`}>
                      <b>{item.name || item.email}</b>
                      {' · '}
                      {item.message}
                    </li>
                  ))}
              </ul>
              {(report.items || []).some((item) => item.reason === 'bootstrap_no_change') && (
                <p>
                  {(report.items || []).filter((item) => item.reason === 'bootstrap_no_change').length} se quedan en el SKU más bajo.
                </p>
              )}
            </div>
          )}

          <div className="impl-action-card__actions lic-plan__actions">
            {isClient ? (
              <>
              <button
                type="button"
                className="impl-btn impl-btn--primary"
                disabled={saving || overQuota || !users.length}
                onClick={handleClientContinue}
                title="Guarda el mapa en Nexus. Plexarian aplica las licencias después."
              >
                <Save size={15} />
                {saving ? 'Guardando…' : dirty ? 'Guardar y continuar' : 'Continuar'}
              </button>
              <button
                type="button"
                className="impl-btn impl-btn--ghost"
                disabled={saving}
                onClick={handleClientSkip}
                title="No es obligatorio ahora. Puedes volver a abrir este mismo enlace más tarde."
              >
                Lo haré después
              </button>
              </>
            ) : (
              <>
            <button
              type="button"
              className={`impl-btn ${dirty ? 'impl-btn--primary' : 'impl-btn--ghost'}`}
              disabled={saving || overQuota || !dirty || applying}
              onClick={handleSave}
              title="Solo guarda el mapa en Nexus. No asigna licencias en Google."
            >
              <Save size={15} />
              {saving ? 'Guardando…' : 'Guardar mapa'}
            </button>
            <button
              type="button"
              className={`impl-btn ${!dirty ? 'impl-btn--primary' : 'impl-btn--ghost'}`}
              disabled={!canApply}
              onClick={handleApply}
                  title="Consulta Reseller. Mueve Workspace y asigna GE Business. Standard y Plus de Gemini no se tocan."
            >
              <Play size={15} />
              {applying ? 'Aplicando…' : 'Aplicar en Google'}
            </button>
            {dirty ? (
              <span className="lic-plan__status">
                {persisted ? 'Guarda el mapa antes de aplicar' : 'Aún no está en Nexus'}
              </span>
            ) : report?.moved > 0 ? (
              <span className="impl-step__badge impl-step__badge--ok">
                <BadgeCheck size={12} /> Aplicado en Google
              </span>
            ) : (
              <span className="impl-step__badge impl-step__badge--ok">
                <BadgeCheck size={12} /> En Nexus · listo para aplicar
              </span>
            )}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}

const LIC_PLAN_CSS = `
.lic-plan { display: flex; flex-direction: column; gap: 10px; min-width: 0; overflow-x: hidden; }
.lic-plan__toolbar {
  display: flex; flex-wrap: wrap; gap: 8px; align-items: center;
  min-width: 0;
}
.lic-plan__quotas { display: flex; flex-wrap: wrap; gap: 6px; }
.lic-plan__chip {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 6px 10px; border-radius: 999px;
  background: #fff; border: 1px solid #E2E8F0; color: #331D40; cursor: pointer;
}
.lic-plan__chip strong { font-size: 0.75rem; font-weight: 700; }
.lic-plan__chip span { font-size: 0.75rem; font-weight: 800; color: #64748B; }
.lic-plan__chip.is-base { background: #F8FAFC; }
.lic-plan__chip.is-over { border-color: #F9A8D4; background: #FFF1F2; color: #BE123C; }
.lic-plan__chip.is-over span { color: #BE123C; }
.lic-plan__chip.is-on { border-color: #FF3364; box-shadow: 0 0 0 1px #FF3364; }
.lic-plan__chip.is-addon { border-style: dashed; }
.lic-plan__error {
  margin: 0; display: flex; align-items: center; gap: 6px;
  font-size: 0.8rem; color: #BE123C; font-weight: 600;
}
.lic-plan__hint {
  margin: 0; font-size: 0.75rem; color: #64748B; font-weight: 600; line-height: 1.35;
}
.lic-plan__search {
  display: flex; align-items: center; gap: 6px; flex: 1; min-width: 180px;
  max-width: 280px; margin-left: auto;
  padding: 6px 10px; border: 1px solid #E2E8F0; border-radius: 10px; background: #fff; color: #64748B;
}
.lic-plan__search input {
  border: none; outline: none; flex: 1; min-width: 0; font-size: 0.82rem; color: #331D40; background: transparent;
}
.lic-plan__bulk { display: flex; flex-wrap: wrap; gap: 6px; align-items: center; font-size: 0.75rem; color: #64748B; width: 100%; }
.lic-plan__list {
  border: 1px solid #E2E8F0; border-radius: 12px; background: #fff; min-width: 0; overflow: hidden;
}
.lic-plan__list-head,
.lic-plan__row {
  display: grid; grid-template-columns: 28px minmax(0, 1fr) auto;
  gap: 10px; align-items: center; min-width: 0;
}
.lic-plan__list-head {
  padding: 8px 12px; border-bottom: 1px solid #E2E8F0; background: #F8FAFC;
  font-size: 0.68rem; letter-spacing: 0.04em; text-transform: uppercase; color: #64748B; font-weight: 700;
}
.lic-plan__col-sku { text-align: right; }
.lic-plan__row {
  padding: 8px 12px; border-bottom: 1px solid #F1F5F9;
}
.lic-plan__row:last-child { border-bottom: none; }
.lic-plan__check { display: grid; place-items: center; margin: 0; }
.lic-plan__who {
  min-width: 0; display: flex; align-items: baseline; gap: 8px;
}
.lic-plan__who strong, .lic-plan__who span {
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.lic-plan__who strong { color: #331D40; font-size: 0.84rem; flex: 0 1 auto; max-width: 42%; }
.lic-plan__who span { color: #64748B; font-size: 0.75rem; min-width: 0; }
.lic-plan__skus { display: flex; flex-wrap: wrap; gap: 6px; justify-self: end; justify-content: flex-end; }
.lic-plan__skus-group { display: flex; flex-wrap: wrap; gap: 4px; justify-content: flex-end; }
.lic-plan__pill {
  appearance: none; border: 1px solid #E2E8F0; background: #F8FAFC;
  color: #331D40; border-radius: 999px; padding: 4px 10px;
  font-size: 0.72rem; font-weight: 700; line-height: 1.2; cursor: pointer;
  white-space: nowrap;
}
.lic-plan__pill.is-on { background: #331D40; color: #fff; border-color: #331D40; }
.lic-plan__pill.is-up.is-on { background: #FF3364; border-color: #FF3364; color: #fff; }
.lic-plan__pill.is-addon { border-style: dashed; }
.lic-plan__pill.is-addon.is-on {
  background: #1A73E8;
  border-color: #1A73E8;
  border-style: solid;
  color: #fff;
}
.lic-plan__chip.is-addon.is-on { border-color: #1A73E8; box-shadow: 0 0 0 1px #1A73E8; }
.lic-plan__status { font-size: 0.75rem; font-weight: 650; color: #B45309; }
.lic-plan__run {
  padding: 10px 12px; border-radius: 12px; background: #fff;
  border: 1px solid #E2E8F0; display: flex; flex-direction: column; gap: 6px;
}
.lic-plan__run-top {
  display: flex; justify-content: space-between; align-items: baseline; gap: 8px;
  font-size: 0.78rem; color: #331D40;
}
.lic-plan__run-top span { color: #64748B; font-weight: 700; }
.lic-plan__run p { margin: 0; font-size: 0.75rem; color: #64748B; }
.lic-plan__bar {
  height: 6px; border-radius: 999px; background: #F1F5F9; overflow: hidden;
}
.lic-plan__bar i {
  display: block; height: 100%; width: 0; background: #FF3364; border-radius: 999px;
  transition: width 0.25s ease;
}
.lic-plan__report {
  padding: 10px 12px; border-radius: 12px; background: #fff;
  border: 1px solid #E2E8F0; display: flex; flex-direction: column; gap: 6px;
}
.lic-plan__report strong { font-size: 0.8rem; color: #331D40; }
.lic-plan__report ul {
  margin: 0; padding: 0; list-style: none; display: flex; flex-direction: column; gap: 3px;
  max-height: 180px; overflow: auto;
}
.lic-plan__report li { font-size: 0.75rem; color: #475569; }
.lic-plan__report li.is-moved { color: #166534; }
.lic-plan__report li.is-failed { color: #BE123C; }
.lic-plan__report p { margin: 0; font-size: 0.72rem; color: #64748B; }
.lic-plan__empty { margin: 0; padding: 16px 12px; font-size: 0.8rem; color: #64748B; }
.lic-plan__pager {
  display: flex; align-items: center; justify-content: space-between; gap: 8px;
  font-size: 0.75rem; color: #64748B;
}
.lic-plan__pager-btns { display: flex; gap: 6px; }
.lic-plan__pager .impl-btn:disabled { opacity: 0.4; cursor: default; }
.lic-plan__actions { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; margin-top: 4px; }
.lic-plan .impl-btn {
  display: inline-flex; align-items: center; gap: 7px; padding: 8px 14px;
  border-radius: 10px; font-size: 0.8rem; font-weight: 700; border: none; cursor: pointer;
  text-decoration: none; color: inherit; font-family: inherit;
}
.lic-plan .impl-btn--primary { background: #FF3364; color: #fff; }
.lic-plan .impl-btn--primary:hover { filter: brightness(0.96); }
.lic-plan .impl-btn--primary:disabled { opacity: 0.65; cursor: wait; }
.lic-plan .impl-btn--ghost {
  background: #fff; color: #331D40; border: 1.5px solid #E2E8F0;
}
.lic-plan .impl-btn--ghost:hover { background: #F8FAFC; }
.lic-plan .impl-btn--icon { padding: 8px; }
.lic-plan .impl-step__badge {
  font-size: 0.65rem; font-weight: 800; letter-spacing: 0.04em; text-transform: uppercase;
  padding: 2px 7px; border-radius: 999px; background: #FFF1F2; color: #BE123C;
  display: inline-flex; align-items: center; gap: 4px;
}
.lic-plan .impl-step__badge--ok { background: #DCFCE7; color: #166534; }
.lic-plan .impl-step__empty { margin: 0; font-size: 0.85rem; color: #64748B; }
@media (max-width: 640px) {
  .lic-plan__search { max-width: none; margin-left: 0; width: 100%; }
  .lic-plan__list-head, .lic-plan__row {
    grid-template-columns: 28px minmax(0, 1fr);
  }
  .lic-plan__col-sku { display: none; }
  .lic-plan__who { flex-direction: column; align-items: flex-start; gap: 1px; }
  .lic-plan__who strong { max-width: 100%; }
  .lic-plan__skus { grid-column: 2; justify-self: start; flex-wrap: wrap; }
}
`;
